import createObjectUrlForFileBlob, { setObjectUrl } from '../actions/createObjectUrlForFileBlob';
import findAttachment from '../utils/findAttachment';
import InlineImageViewState from '../schema/InlineImageViewState';
import loadImage from '../utils/loadImage';
import { AttachmentWellViewState, CreateAttachmentsProperties } from 'owa-attachment-well-data';
import ifIsOverSizeLimitShowErrorMessage from 'owa-attachment-well-data/lib/utils/AttachmentQueueManager/ifIsOverSizeLimitShowErrorMessage';
import { CID_PREFIX, ORIGINAL_SRC_ATTRIBUTE } from '../utils/constants';
import { EditorViewState, PluginWithViewState, PluginViewStateAdapter } from 'owa-editor';
import { format } from 'owa-localize';
import { getPalette } from 'owa-theme';
import { contains, toArray, wrap, unwrap } from 'roosterjs-editor-dom';
import { lazyTryCreateDataURIFromImage } from 'owa-inline-image-loader';
import { isSmimeAttachment } from 'owa-attachment-model-store';
import createLogger, { Logger, LogValueTypes } from '../utils/logger';
import {
    AttachmentFileType,
    AttachmentFileList,
    LocalComputerFile,
    createFileFromImageSource,
} from 'owa-attachment-file-types';
import {
    BeforePasteEvent,
    ChangeSource,
    EditorPlugin,
    IEditor,
    BeforeCutCopyEvent,
    PluginEvent,
    PluginEventType,
} from 'roosterjs-editor-types';
import {
    cleanupImageCopyHandler,
    handleImageCopy,
} from 'owa-inline-image-loader/lib/utils/ImageCopyHandler';
import { ATTRIBUTE_IMAGETYPE } from 'owa-inline-image-loader/lib/utils/Constants';
import safeGetAttribute from 'owa-inline-image-loader/lib/utils/safeGetAttribute';
import getObjectUrl from '../utils/getObjectUrl';

const INVALID_PASTED_URLS_REGEX = /^blob:|^webkit-fake-url:|^file:\/\/\/[a-zA-Z\/]/i;
const ALLOWED_ATTRIBUTES_WHEN_PASTE = ['originalsrc', 'crossorigin', 'contenttype'];
const DOWNLOAD_WILL_EXPIRE_DATASET_NAME = 'willexpire';
const EXPIRABLE_IMG_WRAPPER_HTML =
    '<span style="display: inline-block; width:500px; height: 400px; border: dashed 1px {0}"></span>';
const ADDIN_CONTENT_CHANGE_SOURCE = 'Addins';
const SIGNATURE_CONTENT_CHANGE_SOURCE = 'Signature';
const UPGRADE_CHANGE_SOURCE = 'UpgradeCompose';
const LOAD_IMAGE_CHANGE_SOURCES = [
    ADDIN_CONTENT_CHANGE_SOURCE,
    SIGNATURE_CONTENT_CHANGE_SOURCE,
    UPGRADE_CHANGE_SOURCE,
    ChangeSource.SetContent,
];

const INLINE_ATTACHMENT_PROP: CreateAttachmentsProperties = {
    isInline: true,
    isHiddenInline: false,
    shouldShare: false,
};

export default class InlineImagePlugin
    implements PluginWithViewState<InlineImageViewState>, EditorPlugin {
    private editor: IEditor | null = null;
    private logger: Logger;

    constructor(
        public readonly adapter: PluginViewStateAdapter<InlineImageViewState>,
        public readonly editorAdapter: PluginViewStateAdapter<EditorViewState>,
        private readonly attachmentAdapter: PluginViewStateAdapter<AttachmentWellViewState>,
        private readonly createAttachments: (
            files: AttachmentFileList,
            props: CreateAttachmentsProperties
        ) => void,
        private readonly disableImage?: boolean,
        private readonly userIdentity?: string
    ) {
        this.logger = createLogger(this.editorAdapter().editorId);
    }

    getName() {
        return 'InlineImageV2';
    }

    initialize(editor: IEditor): void {
        this.editor = editor;
        this.logger('lifecycle', 'init');
    }

    dispose(): void {
        this.logger('lifecycle', 'dispose');
        this.editor = null;
        cleanupImageCopyHandler();
    }

    onPluginEvent(event: PluginEvent): void {
        switch (event.eventType) {
            case PluginEventType.EditorReady:
                this.handleInitOrContentChangedEvent();
                break;

            case PluginEventType.ContentChanged:
                if (LOAD_IMAGE_CHANGE_SOURCES.indexOf(event.source) >= 0) {
                    this.handleInitOrContentChangedEvent();
                }
                break;

            case PluginEventType.BeforeCutCopy:
                this.handleBeforeCutCopyEvent(event);
                break;

            case PluginEventType.BeforePaste:
                this.handleBeforePasteEvent(event);
                break;

            case PluginEventType.ExtractContentWithDom:
                this.handleExtractContentEvent(event.clonedRoot);
                break;
        }
    }

    private handleBeforeCutCopyEvent(e: BeforeCutCopyEvent) {
        const images = toArray(e.clonedRoot.querySelectorAll('img'));
        const viewState = this.adapter();
        const attachmentImages: HTMLImageElement[] = [];

        images.forEach(img => {
            if (contains(e.range.commonAncestorContainer, img)) {
                let dataUrl = viewState.objectUrls[img.src];

                if (dataUrl) {
                    img.src = dataUrl;
                    img.removeAttribute(ORIGINAL_SRC_ATTRIBUTE);
                } else {
                    // For other images (loaded from attachment or external), let old copy handler to handle them
                    attachmentImages.push(img);
                }
            }
        });

        if (attachmentImages.length > 0) {
            // Call old copy handler to handle attachment images
            handleImageCopy(attachmentImages);
        }
    }

    private handleBeforePasteEvent(event: BeforePasteEvent) {
        const {
            fragment,
            sanitizingOption: { additionalAllowedAttributes },
        } = event;
        const images = toArray(fragment.querySelectorAll('img'));

        if (fragment.querySelector('span[fluid-url], div[fluid-url]') || images.length == 0) {
            return;
        }

        const viewState = this.adapter();
        const files: LocalComputerFile[] = [];
        const expirableImages: HTMLImageElement[] = [];

        images.forEach(img => {
            const src = img.getAttribute('src');
            let actionType: LogValueTypes['newImage'];

            if (this.disableImage || !src || INVALID_PASTED_URLS_REGEX.test(src)) {
                // Inline image is disabled, or invalid src, remove the element
                img.parentNode?.removeChild(img);
                actionType = this.disableImage ? 'disableImg' : !src ? 'noSrc' : 'invalidSrc';
            } else if (img.dataset[DOWNLOAD_WILL_EXPIRE_DATASET_NAME]?.toLowerCase() == 'true') {
                // Expirable image, need download first
                wrap(img, format(EXPIRABLE_IMG_WRAPPER_HTML, getPalette().themePrimary));
                img.style.maxWidth = '100%';
                img.crossOrigin = 'anonymous';
                delete img.dataset[DOWNLOAD_WILL_EXPIRE_DATASET_NAME];
                expirableImages.push(img);
                actionType = 'downloadExpirableStart';
            } else {
                // New image file to upload
                const file = createFileFromImageSource(src);
                actionType = 'uploadFileFromDataUrl';

                if (file && src) {
                    const objectUrl = createObjectUrlForFileBlob(viewState, file, src);
                    img.setAttribute('src', objectUrl);
                    files.push(this.createLocalFileAttachment(file, objectUrl));
                } else {
                    actionType = 'noFileToUpload';
                }
            }
            this.logger('newImage', actionType);
        });

        const attachmentWell = this.attachmentAdapter();
        if (ifIsOverSizeLimitShowErrorMessage(files, attachmentWell)) {
            // Remove img node(s) from DOM if oversized
            images.forEach(img => {
                img.parentNode?.removeChild(img);
                this.logger('newImage', 'oversizedImg');
            });
        }

        additionalAllowedAttributes.push.apply(
            additionalAllowedAttributes,
            ALLOWED_ATTRIBUTES_WHEN_PASTE
        );

        // We don't await here and just let it continue paste since all the promises won't block pasting.
        // And an event handler of editor plugin shouldn't be async sync editor won't await on it.
        Promise.all(
            expirableImages.map(async img => {
                const dataUri = await this.downloadImageDataURL(img);
                const wrapper = img.parentNode;
                let actionType: LogValueTypes['finishExpirable'];

                if (wrapper && this.editor?.contains(wrapper)) {
                    unwrap(wrapper);
                    const fileBlob = dataUri && createFileFromImageSource(dataUri);

                    if (dataUri && fileBlob) {
                        const objectUrl = createObjectUrlForFileBlob(viewState, fileBlob, dataUri);
                        files.push(this.createLocalFileAttachment(fileBlob, objectUrl));
                        img.src = objectUrl;
                        actionType = 'uploadFileFromExpirable';
                    } else if (!dataUri) {
                        actionType = 'failToDownloadExpirable';
                    } else {
                        actionType = 'noFileToUploadForExpirable';
                    }
                } else {
                    actionType = 'noWrapperForExpirable';
                }

                this.logger('finishExpirable', actionType);
            })
        ).then(() => {
            this.createAttachments(files, INLINE_ATTACHMENT_PROP);
        });
    }

    private async loadObjectUrl(
        attachmentUrl: string,
        viewState: InlineImageViewState,
        img: HTMLImageElement
    ) {
        const objectUrl = await getObjectUrl(attachmentUrl, this.logger);
        if (objectUrl) {
            if (this.editor) {
                img.src = objectUrl;
                setObjectUrl(viewState, objectUrl, attachmentUrl);
            }
        } else {
            img.src = attachmentUrl;
        }
    }

    private handleInitOrContentChangedEvent() {
        const viewState = this.adapter();
        const attachment = this.attachmentAdapter();

        this.editor!.queryElements('img', img => {
            const objectUrl = viewState.objectUrls[img.src] ? img.src : null;
            let actionType: LogValueTypes['showImage'] = 'showObjectUrl';

            if (!objectUrl) {
                const tempImage: HTMLImageElement = img.cloneNode(true) as HTMLImageElement;
                loadImage(tempImage, attachment, this.userIdentity).then(() => {
                    if (this.editor) {
                        const isExternalImage: boolean =
                            safeGetAttribute(img, ATTRIBUTE_IMAGETYPE) === 'External';
                        const attachmentUrl = tempImage.src;
                        if (!isExternalImage) {
                            this.loadObjectUrl(attachmentUrl, viewState, img);
                        } else {
                            img.src = attachmentUrl;
                        }
                    }
                });
                actionType = 'showAttachment';
            }

            this.logger('showImage', actionType);
        });
    }

    private handleExtractContentEvent(root: HTMLElement) {
        const images = toArray(root.querySelectorAll('img'));
        const viewState = this.adapter();
        images.forEach(img => {
            let actionType: LogValueTypes['saveImage'];
            const dataUrl = viewState.objectUrls[img.src];
            if (!dataUrl) {
                actionType = 'noDataUrl';
            } else if (img.getAttribute(ORIGINAL_SRC_ATTRIBUTE)) {
                actionType = 'hasOriginalSrc';
            } else {
                const attachment = findAttachment(this.attachmentAdapter(), {
                    tempId: img.src,
                });

                if (attachment) {
                    const model = attachment.model;
                    const cid = CID_PREFIX + model.ContentId;
                    img.setAttribute(ORIGINAL_SRC_ATTRIBUTE, cid);
                    img.setAttribute('size', model.Size?.toString() || '');
                    img.setAttribute('contentType', model.ContentType || '');

                    // For SMIME attachment, need to replace the SRC attribute with CID so that
                    // it can be correctly encoded.
                    // For regular attachment, we just leave it with object url to make sure it can
                    // be shown correctly in local lie
                    if (isSmimeAttachment(attachment.id.Id)) {
                        img.setAttribute('src', cid);
                    }

                    actionType = 'attachmentFound';
                } else {
                    img.src = dataUrl;
                    actionType = 'fallbackToDataUrl';
                }
            }

            this.logger('saveImage', actionType);
        });
    }

    private createLocalFileAttachment(file: File, objectUrl: string): LocalComputerFile {
        return {
            fileType: AttachmentFileType.Local,
            name: file.name || 'image.png',
            size: file.size,
            type: file.type,
            fileObject: file,
            tempId: objectUrl,
        };
    }

    private downloadImageDataURL(img: HTMLImageElement): Promise<string | null> {
        return new Promise<string | null>(resolve => {
            const removeHandler = () => {
                img.removeEventListener('load', onLoad);
                img.removeEventListener('error', onError);
            };
            const onLoad = () => {
                removeHandler();
                lazyTryCreateDataURIFromImage.import().then(tryCreateDataURIFromImage => {
                    const dataUrl = tryCreateDataURIFromImage(img);
                    resolve(dataUrl);
                });
            };
            const onError = () => {
                removeHandler();
                resolve(null);
            };
            img.addEventListener('load', onLoad);
            img.addEventListener('error', onError);
        });
    }
}
